import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Warranty, WarrantyDocument } from '@wsphere/warranties/domain';
import { WarrantyCustomerPortalService } from '../../../services/warranties.service';

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const FILE_EXTENSIONS: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
};

function validateFile(control: AbstractControl<File | null>): ValidationErrors | null {
  const file = control.value;
  if (!file) return null;
  if (!file.size) return { emptyFile: true };
  if (file.size > MAX_FILE_BYTES) return { fileTooLarge: true };
  const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  return FILE_EXTENSIONS[file.type]?.includes(extension) ? null : { fileType: true };
}

function validateTitle(control: AbstractControl<string>): ValidationErrors | null {
  const title = control.value.trim();
  if (!title) return { required: true };
  return title.length > 255 ? { maxlength: true } : null;
}

function validateDate(control: AbstractControl<string>): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;
  const date = new Date(value + 'T00:00:00Z');
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
    ? null
    : { calendarDate: true };
}

@Component({
  selector: 'ws-upload-warranty-document-dialog',
  templateUrl: './upload-warranty-document.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UploadWarrantyDocumentDialog implements OnDestroy {
  @ViewChild('dialog') dialog?: DialogComponent;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @Output() uploaded = new EventEmitter<string>();

  private currentWarranty?: Warranty.Model;
  private session = 0;

  @Input() set warranty(value: Warranty.Model | undefined) {
    const previousId = this.currentWarranty?.id;
    this.currentWarranty = value;
    if (previousId !== value?.id || value?.status !== Warranty.Status.ACTIVATED) {
      this.reset();
      this.dialog?.close();
    }
  }

  get warranty(): Warranty.Model | undefined {
    return this.currentWarranty;
  }

  readonly categories = Object.values(WarrantyDocument.Category);
  readonly categoryLabels = WarrantyDocument.CATEGORY_LABELS;
  readonly form = new FormGroup({
    title: new FormControl('', { nonNullable: true, validators: [validateTitle] }),
    category: new FormControl<WarrantyDocument.Category | null>(null, Validators.required),
    serviceDate: new FormControl('', { nonNullable: true, validators: [validateDate] }),
    assetId: new FormControl('', { nonNullable: true }),
    file: new FormControl<File | null>(null, [Validators.required, validateFile]),
  });
  submitting = false;
  errors: string[] = [];

  constructor(
    private warrantySvc: WarrantyCustomerPortalService,
    private toastSvc: ToastService,
    private changeDetector: ChangeDetectorRef
  ) {}

  open(): void {
    if (this.warranty?.status !== Warranty.Status.ACTIVATED || this.submitting) return;
    this.reset();
    this.dialog?.open();
  }

  reset(): void {
    this.session++;
    this.submitting = false;
    this.errors = [];
    this.form.enable({ emitEvent: false });
    this.form.reset();
    if (this.fileInput) this.fileInput.nativeElement.value = '';
  }

  selectFile(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;
    this.form.controls.file.setValue(file);
    this.form.controls.file.markAsTouched();
  }

  async submit(): Promise<void> {
    if (this.submitting || this.warranty?.status !== Warranty.Status.ACTIVATED) return;
    this.form.markAllAsTouched();
    const { title, category, serviceDate, assetId, file } = this.form.getRawValue();
    if (assetId && (assetId !== this.warranty.assetId || !this.warranty.asset)) {
      this.form.controls.assetId.setErrors({ warrantyAsset: true });
    }
    if (this.form.invalid || !file || !category) return;

    const session = this.session;
    const warrantyId = this.warranty.id;
    this.submitting = true;
    this.form.disable({ emitEvent: false });
    this.errors = [];
    try {
      await this.warrantySvc.uploadWarrantyDocument(warrantyId, file, {
        title: title.trim(),
        category,
        serviceDate: serviceDate || undefined,
        assetId: assetId || undefined,
      });
      if (session !== this.session) return;
      this.toastSvc.show({
        type: 'success',
        title: 'Document uploaded',
        text: 'Your document has been added to this warranty.',
      });
      this.dialog?.close();
      this.uploaded.emit(warrantyId);
    } catch (error: unknown) {
      if (session !== this.session) return;
      const message: unknown = error instanceof HttpErrorResponse ? error.error?.message : null;
      this.errors = Array.isArray(message)
        ? message.filter((item): item is string => typeof item === 'string' && !!item.trim())
        : typeof message === 'string' && message.trim()
          ? [message]
          : [];
      if (error instanceof HttpErrorResponse && error.status === 413)
        this.errors = ['Choose a file that is 10 MiB or smaller.'];
      if (!this.errors.length) this.errors = ['Unable to upload your document. Please try again.'];
      this.toastSvc.show({
        type: 'error',
        title: 'Upload failed',
        text: 'Your document was not uploaded. Review the message in the dialog and try again.',
      });
    } finally {
      if (session === this.session) {
        this.submitting = false;
        this.form.enable({ emitEvent: false });
        this.changeDetector.markForCheck();
      }
    }
  }

  ngOnDestroy(): void {
    this.session++;
  }
}
